import { ACTIONPLAN_ROUTE_NAMES } from "actionplan/routes/routeNames";
import { BULK_PDF_EXPORT_ROUTE_NAMES } from "bulk-pdf-export/routes/routeNames";
import { CONVERSATION_ROUTE_NAMES } from "conversations/routes/routeNames";
import {
  LESSON_CLASS_ROUTE_NAMES,
  MENTOR_CLASS_ROUTE_NAMES,
} from "classroom/routes/routeNames";
import { ADVISORY_ROUTE_NAMES } from "meetings/advisory/routes/routeNames";
import { GROUP_MEETING_ROUTE_NAMES } from "meetings/group-meeting/routes/routeNames";
import { STUDENT_MEETING_ROUTE_NAMES as STUDENT_FEEDBACK_ROUTE_NAMES } from "meetings/student/routes/routeNames";
import { MY_MEETINGS_ROUTE_NAMES } from "meetings/shared/routes/routeNames";
import { STUDENT_MEETING_ROUTE_NAMES } from "meetings/student-meeting/routes/routeNames";
import { PORTFOLIO_ROUTE_NAMES } from "portfolio/routes/routeNames";
import { ROUTES } from "shared/constants";

export interface NotificationRouteEntry {
  /**
   * Label persisted in the `notifications.route` column at send time.
   * Labels are append-only: old rows keep their label forever, so an entry
   * may only be re-pointed to another route, never removed. The backend
   * source of labels is the `Catwise\Global\Enums\NotificationRoute` enum.
   */
  label: string;
  /** Vue route name to navigate to. */
  name: string;
  /**
   * Older persisted notifications may lack the route param the target needs
   * (e.g. `:mentorClass`): when `param` is absent from the payload, navigate
   * to the legacy `name` instead.
   */
  fallback?: { param: string; name: string };
}

/**
 * Picks the route to navigate to for a map entry: the entry's `name`, or its
 * legacy `fallback.name` when the payload lacks the required route param.
 */
export const resolveNotificationRouteName = (
  entry: NotificationRouteEntry,
  params: Record<string, unknown>,
): string => {
  if (entry.fallback === undefined) {
    return entry.name;
  }

  const paramValue = params[entry.fallback.param];

  if (paramValue === undefined || paramValue === null || paramValue === "") {
    return entry.fallback.name;
  }

  return entry.name;
};

export const NOTIFICATION_ROUTES: NotificationRouteEntry[] = [
  // Shared feedback routes straight to the meeting (/my-feedback/:id, which
  // opens its card on the overview; meeting_id fills :id in the click
  // handler); rows without a meeting_id
  // (e.g. deleted meeting) fall back to the feedback list.
  {
    label: "my-ratings",
    name: STUDENT_FEEDBACK_ROUTE_NAMES.MY_FEEDBACK_DETAIL,
    fallback: { param: "id", name: STUDENT_FEEDBACK_ROUTE_NAMES.MY_FEEDBACK },
  },
  // Same shape as my-ratings: these target /my-meetings/.../:id. Meetings are
  // hard-deleted, so once the row is gone the morph resolves to null and the
  // payload carries no meeting_id — without a fallback router.push throws
  // "Missing required param: id" and the user only sees the generic error toast.
  {
    label: "my-meetings-meetings",
    name: STUDENT_MEETING_ROUTE_NAMES.MY_MEETING,
    fallback: { param: "id", name: MY_MEETINGS_ROUTE_NAMES.MY_MEETINGS },
  },
  {
    label: "my-meetings-transition-meetings",
    name: ADVISORY_ROUTE_NAMES.MY_MEETING,
    fallback: { param: "id", name: MY_MEETINGS_ROUTE_NAMES.MY_MEETINGS },
  },
  {
    label: "my-meetings-group-meetings",
    name: GROUP_MEETING_ROUTE_NAMES.MY_MEETING,
    fallback: { param: "id", name: MY_MEETINGS_ROUTE_NAMES.MY_MEETINGS },
  },
  // Mentor-side labels target the classroom pages; the payload's mentor_class
  // (camelized to mentorClass) is the :mentorClass route param. Old rows
  // without it fall back to the legacy flat pages.
  {
    label: "my-students-meetings",
    name: MENTOR_CLASS_ROUTE_NAMES.STUDENT_RATINGS,
    fallback: {
      param: "mentorClass",
      name: STUDENT_MEETING_ROUTE_NAMES.MY_STUDENTS,
    },
  },
  // No fallback: rows without a mentor_class get the aggregate view — the
  // click handler defaults :mentorClass to AGGREGATE_MENTOR_CLASS (the legacy
  // flat page was removed).
  {
    label: "my-students-group-meetings",
    name: MENTOR_CLASS_ROUTE_NAMES.GROUP_RATINGS,
  },
  {
    label: "my-students-portfolio",
    name: MENTOR_CLASS_ROUTE_NAMES.PORTFOLIO,
    fallback: { param: "mentorClass", name: PORTFOLIO_ROUTE_NAMES.MY_STUDENTS },
  },
  // Cluster-chat notifications carry the receiving teacher's schedule_id; the
  // click handler maps it to :lessonClass (a schedules.id). Old rows without
  // it fall back to the legacy page.
  {
    label: "my-lessongroups-learning-objectives",
    name: LESSON_CLASS_ROUTE_NAMES.LEARNING_GOALS,
    fallback: {
      param: "lessonClass",
      name: ROUTES.MY_LESSONGROUPS_LEARNING_OBJECTIVES,
    },
  },
  // The remaining my-lessongroups-* labels keep pointing at the legacy flat
  // pages: there is no canonical schedule to derive — a (group) meeting spans
  // multiple lesson groups and portfolio is not subject-scoped.
  {
    label: "my-lessongroups-meetings",
    name: STUDENT_MEETING_ROUTE_NAMES.MY_LESSON_GROUPS,
  },
  // The legacy flat page was removed and no canonical schedule can be derived
  // (a group meeting spans multiple lesson groups), so these open the
  // my-meetings group-meeting detail via the payload's meeting_id, like the
  // my-meetings-group-meetings label above.
  {
    label: "my-lessongroups-group-meetings",
    name: GROUP_MEETING_ROUTE_NAMES.MY_MEETING,
    fallback: { param: "id", name: MY_MEETINGS_ROUTE_NAMES.MY_MEETINGS },
  },
  // A group meeting that just closed and now shows in this teacher's lesgroep;
  // the payload's schedule_id becomes :lessonClass in the click handler.
  {
    label: "my-lessongroups-group-ratings",
    name: LESSON_CLASS_ROUTE_NAMES.GROUP_RATINGS,
  },
  {
    label: "my-lessongroups-portfolio",
    name: PORTFOLIO_ROUTE_NAMES.MY_LESSONGROUPS,
  },
  { label: "my-learning-objectives", name: ROUTES.MY_LEARNING_OBJECTIVES },
  {
    label: "my-portfolio",
    name: PORTFOLIO_ROUTE_NAMES.STUDENT_PORTFOLIO_INDEX,
  },
  {
    label: "teacher-scheduled-task",
    name: PORTFOLIO_ROUTE_NAMES.TEACHER_SCHEDULED_TASK,
  },
  {
    label: "student-assignment",
    name: PORTFOLIO_ROUTE_NAMES.STUDENT_ASSIGNMENT,
  },
  { label: "task-slug", name: PORTFOLIO_ROUTE_NAMES.TEACHER_SCHEDULED_TASK },
  { label: "my-actionplans", name: ACTIONPLAN_ROUTE_NAMES.STUDENT_OPEN },
  { label: "actionplans", name: ACTIONPLAN_ROUTE_NAMES.TEACHER_INDEX },
  // A handed-in plan waits on the review page's Te beoordelen tab, not on the
  // overview (ACTIONPLAN-OVERVIEW-TABS-SPLIT-ON-COMPLETION-001).
  {
    label: "actionplan-reviews",
    name: ACTIONPLAN_ROUTE_NAMES.TEACHER_REVIEW_PENDING,
  },
  { label: "manage-bulk-pdfs", name: BULK_PDF_EXPORT_ROUTE_NAMES.MANAGE },
  // Both conversation detail routes take the gesprek as :conversationId, which the
  // payload's conversation_id (camelized) fills. A gesprek can be deleted — by its
  // creator, or with the ingepland gesprek it hangs off — and the row outlives it, so
  // both fall back to their overview rather than letting router.push throw
  // "Missing required param: conversationId".
  {
    label: "my-conversations",
    name: CONVERSATION_ROUTE_NAMES.STUDENT_DETAIL,
    fallback: {
      param: "conversationId",
      name: CONVERSATION_ROUTE_NAMES.STUDENT_OVERVIEW,
    },
  },
  {
    label: "conversations",
    name: CONVERSATION_ROUTE_NAMES.DETAIL,
    fallback: {
      param: "conversationId",
      name: CONVERSATION_ROUTE_NAMES.OVERVIEW,
    },
  },
];
